import { useState, type ReactNode } from "react";
import { audio } from "../lib/audio";
import type { DesktopApp, DesktopConfig } from "../scenario/types";
import { PdfBriefing } from "../ui/PdfBriefing";

/** Icon key -> { glyph, tint }. Crisp DOM tiles, sharp at any 3D zoom. */
const ICONS: Record<string, { glyph: string; tint: string }> = {
  chrome: { glyph: "\u{1F310}", tint: "#3b82f6" },
  mail: { glyph: "\u2709", tint: "#0a84ff" },
  outlook: { glyph: "\u2709", tint: "#0a84ff" },
  files: { glyph: "\u{1F4C1}", tint: "#f4b740" },
  settings: { glyph: "\u2699", tint: "#8a93a0" },
  terminal: { glyph: "\u203A_", tint: "#22c55e" },
  phone: { glyph: "\u{1F4DE}", tint: "#34d399" },
  pdf: { glyph: "\u{1F4C4}", tint: "#e11d48" },
  video: { glyph: "\u25B6", tint: "#0f766e" },
  teams: { glyph: "\u{1F4F9}", tint: "#5b5fc7" },
  // Added for The Message From IT (additive key).
  slack: { glyph: "#", tint: "#4a154b" },
  // Added for Send It Safely (additive keys only).
  guide: { glyph: "\u{1F4D6}", tint: "#0f9d8a" },
  help: { glyph: "?", tint: "#7c5cff" },
  apps: { glyph: "\u{1F50C}", tint: "#e8833a" },
  access: { glyph: "\u{1F512}", tint: "#2f7d6d" },
  share: { glyph: "\u{1F517}", tint: "#2563a8" },
  doc: { glyph: "\u{1F4C4}", tint: "#5b9bff" },
};

function ico(key: string) {
  return ICONS[key] ?? { glyph: "\u25A1", tint: "#6b7480" };
}

/** The four-square Windows mark, drawn in DOM so it stays sharp. */
function WinMark() {
  return (
    <span className="win-mark" aria-hidden="true">
      <i /><i /><i /><i />
    </span>
  );
}

/** Where an open request came from (a desktop icon, a taskbar pin, or the toast). */
export type DesktopOpenVia = "icon" | "taskbar" | "toast";

/**
 * Crisp simulated Windows desktop. Real DOM (sharp at any 3D zoom, accessible):
 * wallpaper, desktop icons, and a Windows-11-style centered taskbar with Start,
 * search, pinned apps (incl. Mail), system tray and clock.
 */
// SIS-DESKTOP-PATCH: every prop below `toast` is optional and unused by other courses.
export function Desktop({
  config,
  onOpen,
  highlightAppId,
  toast,
  children,
  className,
  openAppIds,
  activeAppId,
  badgeAppIds,
}: {
  config: DesktopConfig;
  onOpen: (appId: string, via?: DesktopOpenVia) => void;
  /** app id to pulse (the one the learner should click this beat). */
  highlightAppId?: string;
  /** incoming-message toast (click routes to its app). `calm` drops the blink. */
  toast?: { from: string; subject: string; appId: string; calm?: boolean } | null;
  /** app windows, drawn inside the screen above the icons and below the taskbar. */
  children?: ReactNode;
  /** extra class on the .win screen. */
  className?: string;
  /** apps with an open window: the taskbar shows a running marker. */
  openAppIds?: string[];
  /** the focused app: its taskbar pin is lit. */
  activeAppId?: string | null;
  /** apps with something unread: a red badge on the icon and the pin. */
  badgeAppIds?: string[];
}) {
  const openSet = new Set(openAppIds ?? []);
  const badgeSet = new Set(badgeAppIds ?? []);
  const pinned: DesktopApp[] = config.taskbar?.length ? config.taskbar : config.icons;
  const appIdOf = (a: DesktopApp) => String(a.opens ?? a.id);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const handleOpen = (appId: string, via?: DesktopOpenVia) => {
    audio.ui("appOpen");
    if (appId === "pdf" && config.pdfBriefing) {
      setPdfOpen(true);
      return;
    }
    if (appId === "video" && config.videoBriefing) {
      setVideoOpen(true);
      return;
    }
    onOpen(appId, via);
  };
  const finishVideo = () => {
    setVideoOpen(false);
    onOpen("video_done");
  };
  // Beats that gate on closing the PDF use completeOn.arg="pdf_done" (the id the
  // reader actually emits on close) — but the icon the learner must click is
  // "pdf" itself, so the blink target needs the same alias.
  const isHot = (appId: string) =>
    !!highlightAppId && (appId === highlightAppId || (highlightAppId === "pdf_done" && appId === "pdf") || (highlightAppId === "video_done" && appId === "video"));

  return (
    // A Fragment, not a single wrapping div: PdfBriefing (below) MUST be a
    // SIBLING of .win, not a descendant of it. `.win` gets `filter` from the
    // deviceIn open animation (kept alive at rest by animation-fill-mode:both
    // — a browser CSS quirk where "filter: none" as an animation endpoint is
    // computed back as the identity filter list, not the literal keyword
    // "none"), and ANY computed `filter` on an ancestor creates a NEW
    // containing block for `position:fixed` descendants (same rule as
    // `transform`). PdfBriefing's `.pdfview` relies on `position:fixed;
    // inset:0` meaning "the real viewport" — nested inside `.win` it was
    // instead sized/positioned against `.win`'s own (deliberately
    // monitor-sized, not viewport-sized) box, so tall content's footer/close
    // button could render past `.win`'s edge with no scroll affordance to
    // reach it. This was the actual "too long / cut off / can't close" bug,
    // not just the exposure-map HUD-clearance margin (see styles.css). As a
    // direct child of .device-layer instead, `.pdfview` is genuinely
    // viewport-relative again.
    <>
      <div
        className={`win${className ? ` ${className}` : ""}`}
        style={{
          backgroundImage: config.wallpaperUrl
            ? `url(${config.wallpaperUrl})`
            : "linear-gradient(150deg,#0a4ea3 0%,#1f8fd6 45%,#7fd3e6 100%)",
        }}
      >
      {/* desktop icons (top-left) */}
      <div className="win-dicons">
        {config.icons.map((ic) => {
          const hot = isHot(appIdOf(ic));
          const disabled = !!ic.disabled;
          return (
            <button
              key={ic.id}
              className={`win-dicon ${hot ? "hot" : ""} ${disabled ? "is-disabled" : ""}`}
              type="button"
              disabled={disabled}
              aria-disabled={disabled}
              onClick={() => {
                if (disabled) return;
                handleOpen(appIdOf(ic), "icon");
              }}
            >
              <span className="win-tile" style={{ background: ico(ic.icon).tint }}>
                {ico(ic.icon).glyph}
              </span>
              <span className="win-dicon-label">{ic.label}</span>
              {hot && <span className="win-dicon-hint" aria-hidden="true">Click this</span>}
              {!hot && badgeSet.has(appIdOf(ic)) && <span className="win-dicon-badge" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      {/* incoming message toast */}
      {toast && (
        <button className={`win-toast${toast.calm ? "" : " blink"}`} type="button" onClick={() => handleOpen(toast.appId, "toast")}>
          <span className="win-tile sm" style={{ background: ico("mail").tint }}>
            {ico("mail").glyph}
          </span>
          <span className="win-toast-text">
            <strong>{toast.from}</strong>
            <span>{toast.subject}</span>
          </span>
        </button>
      )}

      {children}

      {/* taskbar */}
      <div className="win-taskbar">
        <button className="win-start" type="button" aria-label="Start" onClick={() => onOpen("__start")}>
          <WinMark />
        </button>

        <div className="win-search">
          <span className="win-search-ico" aria-hidden="true">{"\u{1F50D}"}</span>
          <span className="win-search-ph">Type here to search</span>
        </div>

        <div className="win-pinned">
          {pinned.map((a) => {
            const id = appIdOf(a);
            const hot = isHot(id);
            const disabled = !!a.disabled;
            return (
              <button
                key={a.id}
                className={`win-pin ${hot ? "pulse" : ""} ${disabled ? "is-disabled" : ""}${openSet.has(id) ? " is-open" : ""}${activeAppId === id ? " is-active" : ""}`}
                type="button"
                aria-label={a.label}
                disabled={disabled}
                aria-disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  handleOpen(id, "taskbar");
                }}
              >
                <span className="win-tile sm" style={{ background: ico(a.icon).tint }}>
                  {ico(a.icon).glyph}
                </span>
                {(hot || badgeSet.has(id)) && <span className="win-badge" aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        <div className="win-tray">
          <span className="win-tray-ico">{"\u2227"}</span>
          <span className="win-tray-ico">{"\u{1F4F6}"}</span>
          <span className="win-tray-ico">{"\u{1F50A}"}</span>
          <span className="win-clock">
            <span>{config.clock ?? ""}</span>
            <span>{config.date ?? ""}</span>
          </span>
        </div>
      </div>
      </div>
      {/* Sibling of .win, deliberately outside it — see the Fragment comment
          above for why. */}
      {videoOpen && config.videoBriefing && (
        <div className="desk-film">
          <div className="desk-film-card">
            <video
              src={config.videoBriefing.src}
              poster={config.videoBriefing.poster}
              controls
              autoPlay
              playsInline
              onEnded={finishVideo}
            />
            <button
              type="button"
              className="btn desk-film-skip"
              onClick={(event) => {
                const video = (event.currentTarget.parentElement?.querySelector("video") as HTMLVideoElement | null);
                if (video && Number.isFinite(video.duration) && video.duration > 0) {
                  video.currentTime = Math.max(0, video.duration - 0.05);
                  return;
                }
                finishVideo();
              }}
            >
              Skip to the end
            </button>
          </div>
        </div>
      )}
      {pdfOpen && config.pdfBriefing && (
        <PdfBriefing
          cfg={config.pdfBriefing}
          onClose={() => {
            setPdfOpen(false);
            onOpen("pdf_done");
          }}
        />
      )}
    </>
  );
}
